import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  Subscription
} from 'rxjs'
import {
  bufferCount,
  catchError,
  map,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item/serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item/serie-proposal-item.actions'
import { serieProposalActions } from '../serie-proposal.actions'
import { BulkAction, BulkActionEvent } from './../../../../shared/interfaces'
import { SerieProposalStatus } from './../serie-proposal'
import { SerieProposalEditModalComponent } from '../serie-proposal-edit-modal/show.component'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { SerieProposalItemEditModal } from '../serie-proposal-item/serie-proposal-item-create-edit-modal/show.component'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

export enum ProposalAction {
  DECLINE = 'DECLINE',
  COUNTER_PROPOSAL = 'COUNTER_PROPOSAL'
}
@UntilDestroy()
@Component({
  selector: 'serie-proposal-series-modal',
  templateUrl: './serie-proposal-series-modal.component.html',
  styleUrls: ['./serie-proposal-series-modal.component.scss']
})
export class SerieProposalSeriesModalComponent implements OnInit, OnDestroy {
  @Input() serieProposalItemsWithoutConflicts: SerieProposalItem[] = []
  @Input() serieProposalItemsWithConflicts: SerieProposalItem[] = []
  @Input() internalMemo: string
  @Output() onCounterProposal = new EventEmitter<{
    id: number
    startDate: Date
    endDate: Date
  }>()

  public isLoading: boolean = false
  public form: FormGroup
  public bulkAction = BulkAction
  public refreshData$: Subject<boolean> = new Subject<boolean>()

  private subscriptions: Subscription[] = []

  public serieProposalItemsWithConflictsEnriched$: BehaviorSubject<
    SerieProposalItem[]
  > = new BehaviorSubject<SerieProposalItem[]>([])
  public serieProposalItemsWithConflicts$: BehaviorSubject<
    SerieProposalItem[]
  > = new BehaviorSubject<SerieProposalItem[]>([])
  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]]
    })
    combineLatest([
      this.store.select(serieProposalItemSelectors.selectAll),
      this.serieProposalItemsWithConflicts$
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([serieProposalItems, serieProposalItemsWithConflicts]) => {
        const value = serieProposalItems.filter((serieProposalItem) =>
          this.serieProposalItemsWithConflicts.find(
            (serieProposalItemWithConflicts) =>
              serieProposalItemWithConflicts.id == serieProposalItem.id &&
              serieProposalItemWithConflicts.status !=
                SerieProposalItemStatus.DECLINED
          )
        )
        this.serieProposalItemsWithConflictsEnriched$.next(value)
      })
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    if (bulkActionEvent.action == BulkAction.DECLINE) {
      bulkActionEvent.items.map((item) => {
        this.store.dispatch(
          serieProposalItemActions.updateOneById({
            id: item.id,
            params: {
              id: item.id,
              status: SerieProposalItemStatus.DECLINED
            }
          })
        )
      })
    }

    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        bufferCount(bulkActionEvent.items?.length),
        timeout(5000),
        take(1),
        catchError((error) => {
          this.refreshData$.next(true)
          this.isLoading = false
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.isLoading = false
          return
        }
        if (bulkActionEvent.items?.length > 0) {
          this.store.dispatch(
            serieProposalItemActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  id: {
                    [CondOperator.IN]: bulkActionEvent.items.map(
                      (ite) => ite.id
                    )
                  }
                }
              })
            })
          )
          this.actions$
            .pipe(
              ofType(serieProposalItemActions.upsertMany),
              timeout(5000),
              take(1)
            )
            .subscribe((action) => {
              this.isLoading = false
              this.serieProposalItemsWithConflicts =
                this.serieProposalItemsWithConflicts.filter(
                  (it) => !action.entities.some((entity) => entity.id === it.id)
                )
              this.serieProposalItemsWithConflicts$.next(
                this.serieProposalItemsWithConflicts
              )
              this.refreshData$.next(true)
              this.notificationService.emit('alerts.success', 'success')
            })
        } else {
          this.isLoading = false
          const allEntities = action.flatMap((a) => a.entities)
          this.serieProposalItemsWithConflicts =
            this.serieProposalItemsWithConflicts.filter(
              (it) => !allEntities.some((entity) => entity.id === it.id)
            )
          this.serieProposalItemsWithConflicts$.next(
            this.serieProposalItemsWithConflicts
          )
          this.refreshData$.next(true)
          this.notificationService.emit('alerts.success', 'success')
        }
      })
  }

  public sendCounterProposal(proposalId: number) {
    if (this.form.valid) {
      const { startDate, endDate } = this.form.value
      this.onCounterProposal.emit({
        id: proposalId,
        startDate: moment(startDate).toDate(),
        endDate: moment(endDate).toDate()
      })
    } else {
      this.form.markAllAsTouched()
    }
  }

  public onClickAccept() {
    this.store.dispatch(
      serieProposalActions.updateOneById({
        id: this.serieProposalItemsWithoutConflicts[0].serieProposalId,
        params: {
          status: SerieProposalStatus.ACCEPTED,
          internalMemo: this.internalMemo
        }
      })
    )

    this.actions$
      .pipe(
        ofType(serieProposalActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => {
          this.refreshData$.next(true)
          this.isLoading = false
          return of(null)
        })
      )
      .subscribe((action) => {
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
        }
      })
  }

  public cancel() {
    this.bsModalRef.hide()
  }
}
